import React, {
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useMemo,
  useState,
  useCallback,
} from "react";
import { Container, Graphics } from "@pixi/react";
import { useSelector, useDispatch } from "react-redux";
import { selectHoldAndWinCurrentRound } from "../../State/selectors";
import { reportSymbolReady } from "../../State/actions";
import HoldAndWinSymbolController from "./HoldAndWinSymbolController";
import { safeInit } from "../../utilities";
import { COIN_SYMBOL_IDS } from "../../SymbolData";
import { useHoldAndWinLandingAnimations } from "../../Hooks/useLandingAnimations";

const HoldAndWinSymbol = forwardRef(
  (
    {
      symbolWidth,
      symbolHeight,
      index,
      reelIndex,
      reelSymbolsData,
      symbolId,
      onSymbolStop,
      symbolsSpriteSheet,
      symbolsSpriteSheetBlurred,
      holdAndWinReelRefs,
      y = 0,
    },
    ref
  ) => {
    const symbolControllerRef = useRef(null);
    const symbolsContainerRef = useRef(null); // ONE container only
    const maskRef = useRef(null);
    const [maskReady, setMaskReady] = useState(false);
    const [controllerReady, setControllerReady] = useState(false); // Track when controller is ready
    const reportedReadyRef = useRef(false); // Track if we've reported ready to Redux

    const dispatch = useDispatch();
    const holdAndWinCurrentRound = useSelector(selectHoldAndWinCurrentRound);

    // Get landing animation callback
    const { triggerHoldAndWinSymbolLandingAnimation } =
      useHoldAndWinLandingAnimations();

    // Only apply mask for non-coin symbols (coins use static sprites, no mask needed)
    const isCoin = COIN_SYMBOL_IDS.includes(symbolId);

    // Callback ref to track when mask is ready
    const setMaskRef = useCallback(
      (node) => {
        maskRef.current = node;
        if (node && !maskReady) {
          setMaskReady(true);
        }
      },
      [maskReady]
    );

    // Create mask graphics - only for non-coin symbols (coins use static sprites, no mask needed)
    // Mask should be at y=0 (where visible symbol appears)
    // Visible symbol is at sprite[2] which is at: inner y(-2h) + sprite y(2h) = 0 relative to outer
    const maskGraphics = useMemo(
      () =>
        !isCoin ? (
          <Graphics
            ref={setMaskRef}
            name={`symbol_mask_${reelIndex}_${index}`}
            draw={(g) => {
              g.clear();
              g.beginFill(0x000000, 1);
              // Mask at y=0 (visible symbol position), full size (no clipping)
              g.drawRect(
                -symbolWidth / 2,
                -symbolHeight / 2,
                symbolWidth,
                symbolHeight
              );
              g.endFill();
            }}
          />
        ) : null,
      [setMaskRef, symbolWidth, symbolHeight, reelIndex, index, isCoin]
    );

    // Create HoldAndWinSymbolController once
    useEffect(() => {
      if (symbolsContainerRef.current && !symbolControllerRef.current) {
        safeInit(() => {
          symbolControllerRef.current = new HoldAndWinSymbolController(
            symbolsContainerRef.current, // Single container
            symbolId,
            symbolWidth,
            symbolHeight,
            index,
            reelIndex,
            onSymbolStop, // General stop callback (for game logic)
            triggerHoldAndWinSymbolLandingAnimation, // Landing animation callback
            symbolsSpriteSheet,
            symbolsSpriteSheetBlurred,
            reelSymbolsData,
            holdAndWinReelRefs
          );
          setControllerReady(true); // Signal that controller is ready
          
          // Report to Redux that this symbol is ready
          if (!reportedReadyRef.current) {
            dispatch(reportSymbolReady());
            reportedReadyRef.current = true;
          }
        });
      }

      return () => {
        if (symbolControllerRef.current) {
          symbolControllerRef.current.clearAllTimeouts?.();
          symbolControllerRef.current = null;
          setControllerReady(false);
          reportedReadyRef.current = false;
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Update symbol ID when current round changes
    useEffect(() => {
      if (!symbolControllerRef.current) return;
      if (!holdAndWinCurrentRound?.reels) return;

      const reel = holdAndWinCurrentRound.reels[reelIndex];
      if (!Array.isArray(reel) || index >= reel.length) return;

      const newSymbolId = reel[index];
      if (newSymbolId !== undefined) {
        symbolControllerRef.current.updateSymbolId(newSymbolId);
      }
    }, [holdAndWinCurrentRound, reelIndex, index]);

    // Resize handler (uses new resizeSymbol method matching ReelControllerV2)
    useEffect(() => {
      if (!symbolControllerRef.current) return;
      symbolControllerRef.current.resizeSymbol(symbolHeight);
    }, [symbolHeight]);

    useImperativeHandle(ref, () => ({
      startSpin: () => symbolControllerRef.current?.spin(),
      startSpinTurbo: () => symbolControllerRef.current?.spinTurbo(),
      stopSpin: () => symbolControllerRef.current?.stopSpin(),
      stopSpinTurbo: () => symbolControllerRef.current?.stopSpinTurbo(),
      startTensionSpin: () => symbolControllerRef.current?.startTensionSpin(),
      exitTensionMode: () => symbolControllerRef.current?.exitTensionMode(),
      getSymbolController: () => symbolControllerRef.current,
      isControllerReady: () => controllerReady, // Expose controller ready state
    }), [controllerReady]);

    return (
      <Container
        y={symbolHeight * index}
        name={`hold_and_win_symbol_outer_${index}`}
      >
        {!isCoin && maskGraphics}
        {!isCoin ? (
          // Non-coin symbols: use mask to clip spinning sprites
          <Container
            name={`hold_and_win_symbol_masked_${index}`}
            mask={maskReady ? maskRef.current : null}
          >
            <Container
              y={-symbolHeight * 2}
              name={`hold_and_win_symbol_inner_${index}`}
            >
              <Container
                ref={symbolsContainerRef}
                name={`hold_and_win_symbol_inner_${index}`}
              />
            </Container>
          </Container>
        ) : (
          // Coin symbols: no mask needed (static sprite), container at y=0 (no offset)
          <Container y={0} name={`hold_and_win_symbol_inner_${index}`}>
            <Container
              ref={symbolsContainerRef}
              name={`hold_and_win_symbol_inner_${index}`}
            />
          </Container>
        )}
      </Container>
    );
  }
);

export default React.memo(HoldAndWinSymbol);
